import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { months } from '../../data/events';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const TimelineSlider = ({ currentEventIndex, onEventChange, selectedEvent, allEvents }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderValue, setSliderValue] = useState(currentEventIndex);
  
  const totalEvents = allEvents?.length || 7;

  // Map events to timeline positions
  const getMonthFromDate = (dateStr) => {
    const month = new Date(dateStr).getMonth();
    return month - 2; // Offset since we start from March (index 2)
  };

  // Handle slider change
  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);
    onEventChange(value);
  };

  // Navigate to previous/next event
  const goToPrevEvent = () => {
    if (currentEventIndex > 0) {
      onEventChange(currentEventIndex - 1);
      setSliderValue(currentEventIndex - 1);
    }
  };

  const goToNextEvent = () => {
    if (currentEventIndex < totalEvents - 1) {
      onEventChange(currentEventIndex + 1);
      setSliderValue(currentEventIndex + 1);
    }
  };

  useEffect(() => {
    setSliderValue(currentEventIndex);
  }, [currentEventIndex]);

  return (
    <motion.div
      data-testid="timeline-slider"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="absolute bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-[0_-4px_30px_rgba(0,0,0,0.1)]"
    >
      {/* Current Event Display */}
      <div className="px-4 md:px-6 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.button
              data-testid="prev-event-btn"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={goToPrevEvent}
              disabled={currentEventIndex === 0}
              className={`p-2 rounded-full border border-gray-200 ${
                currentEventIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </motion.button>
            
            <div>
              <p className="font-dm text-xs text-[#666666] uppercase tracking-wider">
                Événement {currentEventIndex + 1}/{totalEvents}
              </p>
              <h3 className="font-league text-lg md:text-xl uppercase tracking-wide text-black">
                {selectedEvent?.name || allEvents?.[currentEventIndex]?.name}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="font-dm text-xs text-[#666666]">
                {selectedEvent?.displayDates || allEvents?.[currentEventIndex]?.displayDates}
              </p>
              <p className="font-dm text-xs text-[#97c7c3] font-medium">
                {selectedEvent?.location || allEvents?.[currentEventIndex]?.location}
              </p>
            </div>
            
            <motion.button
              data-testid="next-event-btn"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={goToNextEvent}
              disabled={currentEventIndex === totalEvents - 1}
              className={`p-2 rounded-full border border-gray-200 ${
                currentEventIndex === totalEvents - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Timeline Slider */}
      <div className="px-4 md:px-6 pb-4">
        <div className="relative">
          {/* Custom slider track */}
          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
            {/* Progress fill */}
            <div 
              className="absolute h-full bg-[#97c7c3] rounded-full transition-all duration-300"
              style={{ width: `${(sliderValue / (totalEvents - 1)) * 100}%` }}
            />
            
            {/* Event markers on track */}
            {allEvents?.map((event, index) => (
              <div
                key={event.id}
                className={`absolute w-3 h-3 rounded-full top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer transition-transform duration-200 hover:scale-125 z-10 ${
                  index === currentEventIndex 
                    ? 'bg-[#baa25e] ring-4 ring-[#baa25e]/30 scale-125' 
                    : index < currentEventIndex 
                      ? 'bg-[#97c7c3]' 
                      : 'bg-gray-300'
                }`}
                style={{ left: `${(index / (totalEvents - 1)) * 100}%` }}
                onClick={() => {
                  onEventChange(index);
                  setSliderValue(index);
                }}
                data-testid={`timeline-marker-${index}`}
              />
            ))}
          </div>

          {/* Hidden range input for accessibility */}
          <input
            type="range"
            min="0"
            max={totalEvents - 1}
            value={sliderValue}
            onChange={handleSliderChange}
            className="absolute inset-0 w-full opacity-0 cursor-pointer"
            data-testid="timeline-range-input"
          />
        </div>

        {/* Month labels */}
        <div className="flex justify-between mt-3 overflow-x-auto no-scrollbar" ref={containerRef}>
          {months.map((month, index) => {
            const hasEvent = allEvents?.some(e => {
              const eventMonth = new Date(e.dates.start).getMonth();
              const monthIndex = index + 2; // March is month 2
              return eventMonth === monthIndex;
            });

            return (
              <div
                key={month.value}
                className="flex flex-col items-center min-w-[60px]"
              >
                <span 
                  className={`font-dm text-xs ${
                    hasEvent ? 'text-black font-medium' : 'text-gray-400'
                  }`}
                >
                  {month.label}
                </span>
                {hasEvent && (
                  <div className="w-1.5 h-1.5 rounded-full bg-[#97c7c3] mt-1" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};
